import { Box, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { caseInsensitiveIncludes, isNotEmptyArray, vyDate } from '@vy/utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useUnmeteredEventsReport } from '../../../../hooks/requests/use-unmetered-events-report';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { ReportTable } from '../../reports-components/ReportTable';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { DisconnectedMeterWarnings } from './DisconnectedMeterWarnings';
import { DisconnectedMeterWarningsToggle } from './DisconnectedMeterWarningsToggle';
import { UnmeteredEventsReportTable } from './UnmeteredEventsReportTable/UnmeteredEventsReportTable';
import { UnmeteredEventsReportToolbar } from './UnmeteredEventsReportToolbar';

export const UnmeteredEventsReportContent: React.FC = () => {
  const apiRef = useGridApiRef();

  const [fromDate, setFromDate] = useState<Date>(() => vyDate().startOf('month').toDate());
  const [toDate, setToDate] = useState<Date>(() => vyDate().endOf('month').toDate());
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [searchText, setSearchText] = useSearchWithSearchParams();

  const { data: report, isLoading } = useUnmeteredEventsReport(fromDate, toDate);

  const disconnectedMeters = report?.disconnectedMeters ?? [];
  const allRows = report?.reportItems ?? [];
  const filterFieldNames = report?.filterFieldNames ?? [];

  const filteredRows = useMemo(() => {
    const query = (searchText ?? '').trim();
    if (query === '') return allRows;
    return allRows.filter(
      (item) =>
        caseInsensitiveIncludes(item.customerAlias, query) || caseInsensitiveIncludes(item.eventName, query),
    );
  }, [allRows, searchText]);

  const handleFromDateChange = useCallback((date: Date | null) => {
    if (date !== null) {
      setFromDate(vyDate(date).startOf('month').toDate());
    }
  }, []);

  const handleToDateChange = useCallback((date: Date | null) => {
    if (date !== null) {
      setToDate(vyDate(date).endOf('month').toDate());
    }
  }, []);

  const exportFnRef = useRef<() => void>(() => undefined);
  const handleExportReady = useCallback((fn: () => void) => {
    exportFnRef.current = fn;
  }, []);
  const handleExport = useCallback(() => exportFnRef.current(), []);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center"
          }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: "20px"
            }}>
            Unmetered Events
          </Typography>
          {isNotEmptyArray(disconnectedMeters) && (
            <DisconnectedMeterWarningsToggle
              disconnectedMeters={disconnectedMeters}
              open={warningsOpen}
              onToggle={() => setWarningsOpen((prev) => !prev)}
            />
          )}
        </Box>
      </Box>
      <DisconnectedMeterWarnings disconnectedMeters={disconnectedMeters} open={warningsOpen} />
      <ReportTable
        title="Unmetered Events"
        hideTitle
        exportCsvFileName="Unmetered Events"
        reportCreationDate={new Date()}
        reportType={REPORT_TYPES.UNMETERED_EVENTS}
        apiRef={apiRef as React.MutableRefObject<GridApiCommunity>}
        hideMenu
        onExportCsv={handleExport}
      >
        <UnmeteredEventsReportToolbar
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          searchValue={searchText ?? ''}
          onSearchChange={setSearchText}
          onExport={handleExport}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <UnmeteredEventsReportTable
            rows={filteredRows}
            filterFieldNames={filterFieldNames}
            isLoading={isLoading}
            apiRef={apiRef as React.MutableRefObject<GridApiPro>}
            onExportReady={handleExportReady}
          />
        </Box>
      </ReportTable>
    </>
  );
};
